import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { Datepicker, type DatepickerOptions } from 'vanillajs-datepicker';

export interface DatePickerProps {
  /** Selected date(s) as a formatted string (matches `format`). Empty string = none. */
  value: string;
  /** Fires with the input's formatted value whenever the selection changes. */
  onChange: (value: string) => void;
  /** Display/parse format (vanillajs-datepicker tokens). Default `yyyy-mm-dd`. */
  format?: string;
  label?: ReactNode;
  /** Leading icon (Phosphor class). Default `ph-calendar`. */
  icon?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Render the calendar inline (attached to a div) instead of a dropdown on an input. */
  inline?: boolean;
  /**
   * Where to append the dropdown calendar. Defaults to `document.body` so it overlays
   * scrolling/stacking containers (e.g. a sidebar). Ignored when `inline`.
   */
  container?: HTMLElement | string;
  className?: string;
  id?: string;

  // --- passthrough picker options ---
  autohide?: boolean;
  clearBtn?: boolean;
  todayBtn?: boolean;
  /** First day of week (0 = Sunday). */
  weekStart?: number;
  calendarWeeks?: boolean;
  daysOfWeekDisabled?: number[];
  datesDisabled?: (string | Date | number)[];
  daysOfWeekHighlighted?: number[];
  minDate?: string | Date | number | null;
  maxDate?: string | Date | number | null;
  /** 0 = day (default), 1 = month, 2 = year. */
  pickLevel?: 0 | 1 | 2;
  /** Opening view: 0 days, 1 months, 2 years, 3 decades. */
  startView?: 0 | 1 | 2 | 3;
  title?: string;
  /** Allow selecting multiple dates (0 = unlimited). */
  maxNumberOfDates?: number;
}

/**
 * React wrapper around vanillajs-datepicker (styled by the Limitless theme). Controlled via
 * `value`/`onChange`; date-only. Use `inline` for an always-open calendar.
 */
export function DatePicker({
  value,
  onChange,
  format = 'yyyy-mm-dd',
  label,
  icon = 'ph-calendar',
  placeholder,
  disabled,
  inline = false,
  container,
  className = '',
  id,
  autohide,
  clearBtn,
  todayBtn,
  weekStart,
  calendarWeeks,
  daysOfWeekDisabled,
  datesDisabled,
  daysOfWeekHighlighted,
  minDate,
  maxDate,
  pickLevel,
  startView,
  title,
  maxNumberOfDates,
}: DatePickerProps) {
  const elRef = useRef<HTMLInputElement & HTMLDivElement>(null);
  const dpRef = useRef<Datepicker | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // Suppress the change handler while we set the date programmatically (avoids a feedback loop).
  const settingRef = useRef(false);

  const options = useMemo<DatepickerOptions>(
    () => ({
      format,
      buttonClass: 'btn',
      prevArrow: '←',
      nextArrow: '→',
      // Append the popover to <body> so it isn't clipped/stacked under scrolling containers.
      ...(!inline && { container: container ?? document.body }),
      ...(autohide != null && { autohide }),
      ...(clearBtn != null && { clearBtn }),
      ...(todayBtn != null && { todayBtn }),
      ...(weekStart != null && { weekStart }),
      ...(calendarWeeks != null && { calendarWeeks }),
      ...(daysOfWeekDisabled && { daysOfWeekDisabled }),
      ...(datesDisabled && { datesDisabled }),
      ...(daysOfWeekHighlighted && { daysOfWeekHighlighted }),
      ...(minDate != null && { minDate }),
      ...(maxDate != null && { maxDate }),
      ...(pickLevel != null && { pickLevel }),
      ...(startView != null && { startView }),
      ...(title != null && { title }),
      ...(maxNumberOfDates != null && { maxNumberOfDates }),
    }),
    [
      format, inline, container, autohide, clearBtn, todayBtn, weekStart, calendarWeeks,
      daysOfWeekDisabled, datesDisabled, daysOfWeekHighlighted, minDate, maxDate, pickLevel,
      startView, title, maxNumberOfDates,
    ],
  );

  const read = (): string => {
    const dp = dpRef.current;
    if (!dp) return '';
    if (inline) {
      const d = dp.getDate(format);
      return typeof d === 'string' ? d : Array.isArray(d) ? d.join(' ') : '';
    }
    return elRef.current?.value ?? '';
  };

  // Instantiate once.
  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    const dp = new Datepicker(el, options);
    dpRef.current = dp;
    if (value) {
      settingRef.current = true;
      dp.setDate(value);
      settingRef.current = false;
    }
    const handler = () => {
      if (settingRef.current) return;
      onChangeRef.current(read());
    };
    el.addEventListener('changeDate', handler);
    return () => {
      el.removeEventListener('changeDate', handler);
      dp.destroy();
      dpRef.current = null;
    };
    // Construct once; option/value changes are handled by the effects below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply option changes.
  useEffect(() => {
    dpRef.current?.setOptions(options);
  }, [options]);

  // Reflect external value changes.
  useEffect(() => {
    const dp = dpRef.current;
    if (!dp) return;
    if (read() === value) return;
    settingRef.current = true;
    if (value) dp.setDate(value);
    else dp.setDate({ clear: true });
    settingRef.current = false;
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  if (inline) {
    return <div ref={elRef} className={className} />;
  }

  return (
    <div className={`mb-3 ${className}`.trim()}>
      {label && (
        <label className="form-label" htmlFor={id}>
          {label}
        </label>
      )}
      <div className="input-group">
        <span
          className="input-group-text"
          style={disabled ? undefined : { cursor: 'pointer' }}
          onClick={() => {
            if (disabled) return;
            elRef.current?.focus();
            dpRef.current?.show();
          }}
        >
          <i className={icon} />
        </span>
        <input
          ref={elRef}
          id={id}
          type="text"
          className="form-control"
          placeholder={placeholder}
          disabled={disabled}
          defaultValue={value}
          onClick={() => dpRef.current?.show()}
        />
      </div>
    </div>
  );
}
